import { useCallback, useState } from "react";
import type { AttachmentDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";

export function DeleteAttachmentDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: AttachmentDto | null;
  onClose(): void;
  onDeleted(): Promise<void>;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const confirm = useCallback(async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.deleteAttachment(target.id, reason);
      push(
        t("documents.deletedToast"),
        t("documents.deletedToastDesc", { name: target.originalName })
      );
      setReason("");
      onClose();
      await onDeleted();
    } catch (err) {
      if (err instanceof ApiClientError) {
        push(
          err.message,
          err.requestId ? t("common.requestId", { id: err.requestId }) : undefined,
          "danger"
        );
      }
    } finally {
      setBusy(false);
    }
  }, [onClose, onDeleted, push, reason, t, target]);

  return (
    <Modal
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t("documents.deleteTitle", {
        name: target?.originalName ?? t("documents.document"),
      })}
      description={t("documents.deleteDescription")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            disabled={busy || reason.trim().length < 3}
          >
            {t("documents.confirmDelete")}
          </Button>
        </>
      }
    >
      <TextInput
        label={t("common.fields.reason")}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        hint={t("documents.reasonHint")}
        error={null}
      />
    </Modal>
  );
}
